import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Calendar as CalendarIcon, Clock, User, Star } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { SERVICES, SERVICE_BY_SLUG, COUNTRIES, TIME_SLOTS } from "@/data/site";
import { api, formatINR, formatApiErr } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";

const STEPS = ["Service", "Date & Time", "Your Details", "Confirm & Pay"];

export default function Booking() {
    const [params] = useSearchParams();
    const presetSlug = params.get("service");
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        service_slug: presetSlug || "",
        date: null, // Date obj
        time_slot: "",
        name: "",
        phone: "",
        email: "",
        country: "India",
        gender: "",
        birth_date: "",
        birth_time: "",
        birth_place: "",
        message: "",
    });

    const selected = SERVICE_BY_SLUG[form.service_slug];

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const canNext = useMemo(() => {
        if (step === 0) return !!form.service_slug;
        if (step === 1) return !!form.date && !!form.time_slot;
        if (step === 2) return form.name && form.email && form.phone && form.country;
        return true;
    }, [step, form]);

    const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
    const goPrev = () => setStep((s) => Math.max(s - 1, 0));

    const submit = async () => {
        if (!form.service_slug || !form.date) return;
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                date: format(form.date, "yyyy-MM-dd"),
            };
            const { data: booking } = await api.post("/bookings", payload);
            const origin_url = window.location.origin;
            const { data: checkout } = await api.post("/payments/checkout", {
                booking_id: booking.id,
                origin_url,
            });
            window.location.href = checkout.checkout_url;
        } catch (e) {
            toast.error(formatApiErr(e.response?.data?.detail) || "Booking failed. Please try again.");
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="booking-page" className="pt-32 pb-20 min-h-screen">
            <div className="container-vv max-w-4xl">
                <div className="label-eyebrow text-center">Book a Consultation</div>
                <h1 className="font-serif text-4xl sm:text-5xl text-center text-cream mt-3">Schedule your session</h1>

                {/* Stepper */}
                <div className="mt-12 flex items-center justify-center gap-2 md:gap-4" data-testid="booking-stepper">
                    {STEPS.map((label, i) => (
                        <div key={label} className="flex items-center gap-2 md:gap-4">
                            <div className={`flex items-center gap-2 ${i === step ? "text-gold" : i < step ? "text-cream" : "text-cream-dim"}`}>
                                <div className={`h-8 w-8 rounded-full grid place-items-center border ${
                                    i === step ? "bg-gold text-ink border-gold" : i < step ? "border-gold text-gold" : "border-cream-dim/40"
                                } text-xs font-medium`}>
                                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                                </div>
                                <span className="hidden md:inline text-xs tracking-widest uppercase">{label}</span>
                            </div>
                            {i < STEPS.length - 1 && <div className={`h-px w-6 md:w-12 ${i < step ? "bg-gold" : "bg-cream-dim/30"}`} />}
                        </div>
                    ))}
                </div>

                <div className="mt-12 card-luxe min-h-[500px]">
                    {/* Step 0: Service */}
                    {step === 0 && (
                        <div data-testid="step-service">
                            <h2 className="font-serif text-2xl text-cream mb-6">Choose your service</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {SERVICES.map((s) => {
                                    const Icon = s.icon;
                                    const active = form.service_slug === s.slug;
                                    return (
                                        <button
                                            key={s.slug}
                                            type="button"
                                            onClick={() => update("service_slug", s.slug)}
                                            data-testid={`select-service-${s.slug}`}
                                            className={`text-left rounded-xl border p-5 transition-all ${
                                                active ? "border-gold bg-gold/10" : "border-gold/15 bg-ink-deep/40 hover:border-gold/40"
                                            }`}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <Icon className="h-5 w-5 text-gold" />
                                                {active && <Check className="h-4 w-4 text-gold" />}
                                            </div>
                                            <div className="font-serif text-lg text-cream">{s.name}</div>
                                            <div className="text-xs text-cream-muted mt-1">{s.duration}</div>
                                            <div className="mt-3 font-serif text-gold">{formatINR(s.priceInr)}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Step 1: Date & Time */}
                    {step === 1 && (
                        <div data-testid="step-datetime">
                            <h2 className="font-serif text-2xl text-cream mb-6">Pick a date & time</h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <div className="flex items-center gap-2 mb-3 text-gold"><CalendarIcon className="h-4 w-4" /><span className="text-sm tracking-wider uppercase">Date</span></div>
                                    <div className="rounded-xl border border-gold/20 bg-ink-deep/40 p-3">
                                        <Calendar
                                            mode="single"
                                            selected={form.date}
                                            onSelect={(d) => update("date", d)}
                                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                            data-testid="booking-calendar"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-3 text-gold"><Clock className="h-4 w-4" /><span className="text-sm tracking-wider uppercase">Time slot (IST)</span></div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {TIME_SLOTS.map((t) => (
                                            <button
                                                key={t}
                                                type="button"
                                                onClick={() => update("time_slot", t)}
                                                data-testid={`time-${t.replace(/[^0-9A-Z]/gi, "")}`}
                                                className={`text-xs py-2 rounded-md border transition-all ${
                                                    form.time_slot === t ? "border-gold bg-gold text-ink" : "border-gold/20 text-cream hover:border-gold/50"
                                                }`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Details */}
                    {step === 2 && (
                        <div data-testid="step-details">
                            <h2 className="font-serif text-2xl text-cream mb-6">Your details</h2>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Full name *</label>
                                    <input className="input-luxe mt-2" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="input-name" />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Email *</label>
                                    <input type="email" className="input-luxe mt-2" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="input-email" />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Phone (w/ code) *</label>
                                    <input className="input-luxe mt-2" required placeholder="+91 98711 78773" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="input-phone" />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Country *</label>
                                    <select className="input-luxe mt-2" value={form.country} onChange={(e) => update("country", e.target.value)} data-testid="input-country">
                                        {COUNTRIES.map((c) => <option key={c} value={c} className="bg-ink">{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Gender</label>
                                    <select className="input-luxe mt-2" value={form.gender} onChange={(e) => update("gender", e.target.value)} data-testid="input-gender">
                                        <option value="" className="bg-ink">Select</option>
                                        <option value="Female" className="bg-ink">Female</option>
                                        <option value="Male" className="bg-ink">Male</option>
                                        <option value="Other" className="bg-ink">Other / Prefer not to say</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Birth date</label>
                                    <input type="date" className="input-luxe mt-2" value={form.birth_date} onChange={(e) => update("birth_date", e.target.value)} data-testid="input-birth-date" />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Birth time</label>
                                    <input type="time" className="input-luxe mt-2" value={form.birth_time} onChange={(e) => update("birth_time", e.target.value)} data-testid="input-birth-time" />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Birth place</label>
                                    <input className="input-luxe mt-2" placeholder="City, State, Country" value={form.birth_place} onChange={(e) => update("birth_place", e.target.value)} data-testid="input-birth-place" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs tracking-widest uppercase text-cream-muted">Message / Specific question</label>
                                    <textarea rows={3} className="input-luxe mt-2" value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="input-message" placeholder="Anything specific you'd like Dr. Tannu to focus on?" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Confirm */}
                    {step === 3 && (
                        <div data-testid="step-confirm">
                            <h2 className="font-serif text-2xl text-cream mb-6">Confirm & Pay</h2>
                            <div className="space-y-5 text-sm">
                                <Row label="Service" value={selected?.name} />
                                <Row label="Duration" value={selected?.duration} />
                                <Row label="Date" value={form.date ? format(form.date, "EEEE, dd MMM yyyy") : "—"} />
                                <Row label="Time" value={form.time_slot} />
                                <Row label="Name" value={form.name} />
                                <Row label="Email" value={form.email} />
                                <Row label="Phone" value={form.phone} />
                                <Row label="Country" value={form.country} />
                                {form.birth_date && <Row label="Birth" value={`${form.birth_date} ${form.birth_time || ""} · ${form.birth_place || "—"}`} />}
                                <div className="pt-4 border-t border-gold/15 flex items-center justify-between">
                                    <span className="font-serif text-lg text-cream">Total</span>
                                    <span className="font-serif text-2xl text-gold">{selected ? formatINR(selected.priceInr) : "—"}</span>
                                </div>
                                <div className="text-xs text-cream-muted mt-3 flex items-center gap-2">
                                    <Star className="h-3.5 w-3.5 text-gold" /> Secure Stripe checkout · Card, UPI, Netbanking, Wallets accepted
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer nav */}
                <div className="mt-8 flex items-center justify-between gap-4">
                    <button
                        type="button"
                        onClick={goPrev}
                        disabled={step === 0}
                        className="btn-outline-gold text-sm disabled:opacity-30"
                        data-testid="booking-prev-btn"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    {step < STEPS.length - 1 ? (
                        <button type="button" disabled={!canNext} onClick={goNext} className="btn-gold text-sm disabled:opacity-50" data-testid="booking-next-btn">
                            Continue <ArrowRight className="h-4 w-4" />
                        </button>
                    ) : (
                        <button type="button" disabled={submitting} onClick={submit} className="btn-gold text-sm disabled:opacity-50" data-testid="booking-pay-btn">
                            {submitting ? "Redirecting..." : <>Pay & Confirm <ArrowRight className="h-4 w-4" /></>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 text-cream">
        <span className="text-cream-muted text-xs tracking-widest uppercase">{label}</span>
        <span className="text-right">{value || "—"}</span>
    </div>
);
